/**
 * Layout Registry
 *
 * Central registry for all available pathway layout algorithms.
 * Allows easy switching between different layout types.
 */

import layoutOrganicTree from './organicTree';
import layoutRoute from './route';

export const LAYOUT_TYPES = {
  ORGANIC_TREE: 'organic-tree',
  // One lane per team: root left, goal right, milestones by date between
  // (settings.impactMapLayout = 'route'; see route.js).
  ROUTE: 'route',
};

/** The layout an instance asked for in `settings.impactMapLayout`; organic tree otherwise. */
export const layoutForSettings = (settings) => (
  settings?.impactMapLayout === LAYOUT_TYPES.ROUTE ? LAYOUT_TYPES.ROUTE : LAYOUT_TYPES.ORGANIC_TREE
);

/**
 * Registry mapping layout type to layout function
 */
const layoutRegistry = {
  [LAYOUT_TYPES.ORGANIC_TREE]: layoutOrganicTree,
  [LAYOUT_TYPES.ROUTE]: layoutRoute,
};

/**
 * Get a layout function by type
 *
 * @param {string} layoutType - Type from LAYOUT_TYPES
 * @returns {Function} Layout function
 */
export function getLayout(layoutType) {
  const layout = layoutRegistry[layoutType];
  if (!layout) {
    console.warn(`Layout type "${layoutType}" not found, using default`);
    return layoutRegistry[LAYOUT_TYPES.ORGANIC_TREE];
  }
  return layout;
}

/**
 * Apply a layout to a set of nodes
 *
 * @param {Array} nodes - Array of node objects
 * @param {string} layoutType - Type from LAYOUT_TYPES
 * @param {Object} config - Optional layout-specific config
 * @returns {Object} {positions: {nodeId: {x, y}}, arrows: [{from, to, path}]}
 */
export function applyLayout(nodes, layoutType = LAYOUT_TYPES.ORGANIC_TREE, config = {}) {
  const layoutFn = getLayout(layoutType);
  return layoutFn(nodes, config);
}

export default {
  LAYOUT_TYPES,
  getLayout,
  applyLayout
};
